import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Globe, MapPin, Server, ShieldCheck, AlertTriangle, RefreshCw } from 'lucide-react';

export const MyIPChecker = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchIP = async () => {
        setLoading(true);
        setError(null);
        try {
            // Using ipapi.co for detailed info (free tier)
            const response = await fetch('https://ipapi.co/json/');
            if (!response.ok) throw new Error('Failed to fetch IP data');
            const result = await response.json();
            setData(result);
        } catch (err) {
            console.error(err);
            // Fallback to simpler API if rate limited
            try {
                const fallback = await fetch('https://api.ipify.org?format=json');
                const fallbackData = await fallback.json();
                setData({ ip: fallbackData.ip, city: 'Unknown', country_name: 'Unknown', org: 'Unknown' });
            } catch (e) {
                setError('Unable to detect IP address. Check your ad blocker.');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchIP();
    }, []);

    const isSecure = data?.org && (
        data.org.toLowerCase().includes('vpn') ||
        data.org.toLowerCase().includes('hosting') ||
        data.org.toLowerCase().includes('cloud')
    );

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
                <div className="p-4 bg-blue-500/10 rounded-2xl border border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.2)]">
                    <Globe className="w-8 h-8 text-blue-400" />
                </div>
                <div>
                    <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60">My IP Info</h2>
                    <p className="text-muted-foreground">Check your public IP, location, and ISP details.</p>
                </div>
            </div>

            <div className="glass p-8 rounded-2xl border border-white/10 relative overflow-hidden">
                {/* Background decorative glow */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-[80px] pointer-events-none" />

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-12 space-y-4">
                        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                        <p className="text-muted-foreground animate-pulse">Scanning network...</p>
                    </div>
                ) : error ? (
                    <div className="text-center py-8 text-red-400">
                        <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-80" />
                        <p>{error}</p>
                        <button onClick={fetchIP} className="mt-4 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm">Retry</button>
                    </div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="grid grid-cols-1 md:grid-cols-2 gap-8"
                    >
                        {/* Main IP Display */}
                        <div className="md:col-span-2 text-center py-6 border-b border-white/5">
                            <span className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-2 block">Your Public IPv4</span>
                            <div className="text-5xl md:text-6xl font-mono font-bold text-white tracking-tight drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]">
                                {data.ip}
                            </div>
                        </div>

                        {/* Details */}
                        <div className="space-y-4">
                            <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/5">
                                <MapPin className="w-5 h-5 text-primary mt-1" />
                                <div>
                                    <span className="text-xs text-muted-foreground uppercase font-bold">Location</span>
                                    <div className="text-lg font-semibold">{data.city}, {data.region}</div>
                                    <div className="text-sm text-gray-400">{data.country_name}</div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/5">
                                <Server className="w-5 h-5 text-purple-400 mt-1" />
                                <div>
                                    <span className="text-xs text-muted-foreground uppercase font-bold">ISP / Organization</span>
                                    <div className="text-lg font-semibold">{data.org || data.asn}</div>
                                    <div className="text-sm text-gray-400">Network Provider</div>
                                </div>
                            </div>
                        </div>

                        {/* VPN Status Hint */}
                        <div className="md:col-span-2">
                            {isSecure ? (
                                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center gap-3">
                                    <ShieldCheck className="w-6 h-6 text-green-400" />
                                    <div>
                                        <h4 className="font-bold text-green-400">Privacy Status: Likely Protected</h4>
                                        <p className="text-xs text-green-300/80">Your IP appears to belong to a known datacenter/VPN service.</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl flex items-center gap-3">
                                    <AlertTriangle className="w-6 h-6 text-yellow-400" />
                                    <div>
                                        <h4 className="font-bold text-yellow-400">Privacy Status: Exposed</h4>
                                        <p className="text-xs text-yellow-300/80">This appears to be a residential ISP IP. Your location is visible.</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="md:col-span-2 flex justify-center">
                            <button
                                onClick={fetchIP}
                                className="flex items-center gap-2 px-6 py-2 rounded-full bg-white/5 hover:bg-white/10 text-sm font-medium transition-colors"
                            >
                                <RefreshCw className="w-4 h-4" /> Refresh IP Data
                            </button>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
};
